import { useEffect, useState, useMemo } from 'react';
import { Repeat, Flame, Trophy, TrendingUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api, setRecurrenceOverride } from '../../lib/api';
import { AREE } from '../../constants/areas';

/**
 * HabitsHeatmap90 — heatmap stile GitHub Contributions per le abitudini ricorrenti.
 *
 * Layout: per ogni "abitudine" (MIT/Azione con recurrence_rule), mostra una
 * mini-griglia 13 colonne (settimane) × 7 righe (giorni della settimana) sui
 * passati N giorni (default 90).
 *
 * Stati per cella:
 *  - vuoto (occorrenza non prevista): griglia trasparente con bordo
 *  - prevista non fatta: rosa chiaro (warning) / o grigio neutro
 *  - prevista fatta: verde con intensità in base all'età (più recente = più scuro)
 *  - cancellata: barrata
 *  - oggi: bordo ink
 *
 * Interazione: click su una cella prevista toggle l'override (fatto sì/no).
 */
export default function HabitsHeatmap90() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/stats/habits-heatmap', { params: { days: 90 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'errore'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);

  if (loading) {
    return <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-mute py-6" data-testid="habits-loading">Caricamento abitudini…</div>;
  }
  if (error) {
    return <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-accent py-6">Errore · {error}</div>;
  }
  if (!data || data.habits.length === 0) {
    return (
      <div className="mt-12 mb-4 py-10 px-6 border border-line bg-tint" data-testid="habits-empty">
        <div className="flex items-start gap-3">
          <Sparkles size={18} strokeWidth={1.5} className="text-mute shrink-0 mt-1" />
          <div>
            <h3 className="font-display text-lg tracking-tight mb-1">Nessuna abitudine ancora</h3>
            <p className="font-body text-sm text-soft max-w-xl">
              Imposta una ricorrenza (icona <Repeat size={11} className="inline" /> accanto a una MIT o Azione)
              per vedere qui la heatmap degli ultimi 90 giorni — perfetta per tracciare cose tipo &quot;Palestra ogni giorno&quot;, &quot;Meditazione lun-mer-ven&quot;, &quot;Pagamenti ogni 15&quot;.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const today = data.today;

  const handleCellClick = async (habit, day) => {
    if (!day.occurred || day.cancelled) return;
    // Optimistic toggle
    const next = !day.done;
    setData((d) => ({
      ...d,
      habits: d.habits.map((h) => h.id !== habit.id ? h : ({
        ...h,
        days: h.days.map((x) => x.date === day.date ? { ...x, done: next } : x),
        stats: { ...h.stats, done: h.stats.done + (next ? 1 : -1), rate: Math.max(0, Math.min(1, (h.stats.done + (next ? 1 : -1)) / Math.max(1, h.stats.total_occurrences))) },
      })),
    }));
    try {
      await setRecurrenceOverride({
        parent_kind: habit.kind,
        parent_id: habit.id,
        occurrence_date: day.date,
        fatto: next,
      });
      // Refresh stats (streak) dal backend in background
      setRefresh((x) => x + 1);
    } catch (e) {
      // Rollback
      setData((d) => ({
        ...d,
        habits: d.habits.map((h) => h.id !== habit.id ? h : ({
          ...h,
          days: h.days.map((x) => x.date === day.date ? { ...x, done: !next } : x),
        })),
      }));
      toast.error(e?.response?.data?.detail || 'Salvataggio fallito');
    }
  };

  return (
    <div className="mt-10" data-testid="habits-heatmap-section">
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
          <Repeat size={18} strokeWidth={1.5} />
          Abitudini · ultimi {data.days} giorni
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
          {data.habits.length} {data.habits.length === 1 ? 'serie' : 'serie'} · dal {data.from}
        </span>
      </div>
      <div className="space-y-6">
        {data.habits.map((h) => (
          <HabitRow key={`${h.kind}-${h.id}`} habit={h} today={today} onCellClick={handleCellClick} />
        ))}
      </div>
    </div>
  );
}

function HabitRow({ habit, today, onCellClick }) {
  // Griglia: organizza i giorni per "settimana" (7 colonne). Inizia dal lunedì.
  const grid = useMemo(() => buildWeekGrid(habit.days), [habit.days]);
  const meta = AREE[(habit.area || '').toLowerCase()] || { color: 'var(--qp-text-soft)' };
  const { stats } = habit;

  return (
    <div className="border border-line p-4 bg-canvas" data-testid={`habit-${habit.kind}-${habit.id}`}>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span
            className="shrink-0 w-1 h-8 inline-block"
            style={{ backgroundColor: meta.color }}
            aria-hidden
          />
          <div className="min-w-0">
            <div className="font-display text-base tracking-tight truncate" title={habit.testo}>
              {habit.testo || '(senza testo)'}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute mt-0.5 inline-flex items-center gap-2">
              <Repeat size={10} strokeWidth={1.5} /> {habit.rule_label}
              {habit.kind === 'azione' && <span>· Azione</span>}
              {habit.kind === 'mit' && <span>· MIT</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 font-mono text-[11px] tabular-nums">
          <Stat icon={<TrendingUp size={11} strokeWidth={1.5} />} label="Completion" value={`${(stats.rate * 100).toFixed(0)}%`} testid={`habit-rate-${habit.id}`} />
          <Stat icon={<Flame size={11} strokeWidth={1.5} className="text-accent" />} label="Streak" value={`${stats.current_streak}`} testid={`habit-streak-${habit.id}`} />
          <Stat icon={<Trophy size={11} strokeWidth={1.5} className="text-[#D97706]" />} label="Best" value={`${stats.longest_streak}`} testid={`habit-best-${habit.id}`} />
          <div className="text-soft">
            <span className="text-ink">{stats.done}</span><span className="text-mute">/{stats.total_occurrences}</span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="inline-grid gap-[2px]" style={{ gridTemplateRows: 'repeat(7, 12px)', gridAutoFlow: 'column', gridAutoColumns: '12px' }}>
          {grid.flat().map((cell, idx) => (
            <Cell key={cell?.date || `pad-${idx}`} cell={cell} today={today} onClick={() => cell && onCellClick(habit, cell)} testid={`habit-cell-${habit.id}-${cell?.date || idx}`} />
          ))}
        </div>
      </div>
      <Legend />
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="inline-flex items-center gap-1.5" title={label} data-testid={testid}>
      {icon}
      <span className="text-ink font-medium">{value}</span>
      <span className="text-mute text-[9px] uppercase tracking-[0.15em]">{label}</span>
    </div>
  );
}

function Cell({ cell, today, onClick }) {
  if (!cell) {
    return <div className="w-3 h-3" style={{ backgroundColor: 'transparent' }} />;
  }
  const isToday = cell.date === today;
  let bg = 'transparent';
  let border = '1px solid var(--qp-border)';
  let extra = '';
  if (cell.cancelled) {
    bg = 'repeating-linear-gradient(45deg, rgb(var(--qp-risk-rgb) / 0.15), rgb(var(--qp-risk-rgb) / 0.15) 2px, transparent 2px, transparent 4px)';
    border = '1px solid rgb(var(--qp-risk-rgb) / 0.45)';
  } else if (cell.occurred) {
    if (cell.done) {
      // Verde intenso per "fatto"
      bg = 'rgb(var(--qp-success-rgb) / 0.85)';
      border = '1px solid transparent';
    } else {
      // Previsto non fatto: rosa chiaro
      bg = 'rgb(var(--qp-risk-rgb) / 0.40)';
      border = '1px solid rgb(var(--qp-risk-rgb) / 0.45)';
    }
  }
  const tooltip = (() => {
    const base = `${cell.date}`;
    if (!cell.occurred) return `${base} · non prevista`;
    if (cell.cancelled) return `${base} · cancellata`;
    return `${base} · ${cell.done ? 'fatta ✓' : 'da fare'} (click per toggle)`;
  })();
  return (
    <button
      type="button"
      onClick={cell.occurred && !cell.cancelled ? onClick : undefined}
      className={'w-3 h-3 transition-transform hover:scale-125 ' + (cell.occurred && !cell.cancelled ? 'cursor-pointer' : 'cursor-default')}
      style={{
        background: bg,
        border,
        outline: isToday ? '1.5px solid var(--qp-ink)' : 'none',
        outlineOffset: isToday ? '1px' : '0',
      }}
      title={tooltip}
      aria-label={tooltip}
    />
  );
}

function Legend() {
  return (
    <div className="mt-3 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.18em] text-mute">
      <span>Meno</span>
      <div className="flex gap-[2px]">
        <span className="inline-block w-3 h-3" style={{ background: 'transparent', border: '1px solid var(--qp-border)' }} />
        <span className="inline-block w-3 h-3" style={{ background: 'rgb(var(--qp-risk-rgb) / 0.40)', border: '1px solid rgb(var(--qp-risk-rgb) / 0.45)' }} />
        <span className="inline-block w-3 h-3" style={{ background: 'rgb(var(--qp-success-rgb) / 0.85)' }} />
      </div>
      <span>Più</span>
      <span className="ml-4">non prevista · da fare · fatta</span>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Organizza una sequenza di giorni in una griglia 7 righe (Lun→Dom) × N colonne.
 * Le celle vuote ai bordi (prima del primo lunedì o dopo l'ultima dom) sono `null`.
 */
function buildWeekGrid(days) {
  if (!days || days.length === 0) return [];
  const first = new Date(days[0].date + 'T00:00:00');
  // Quanti giorni di padding prima del primo lunedì?
  // ISO weekday: Mon=1 … Sun=7 — vogliamo offset = weekday-1
  const weekday = first.getDay() === 0 ? 7 : first.getDay(); // 1..7 (Mon..Sun)
  const padBefore = weekday - 1;
  const cells = Array(padBefore).fill(null).concat(days);
  // Pad alla fine fino a multiplo di 7
  while (cells.length % 7 !== 0) cells.push(null);
  // Riorganizza in colonne (settimane) × 7 righe
  const cols = cells.length / 7;
  const grid = [];
  for (let c = 0; c < cols; c++) {
    const colCells = [];
    for (let r = 0; r < 7; r++) {
      colCells.push(cells[c * 7 + r]);
    }
    grid.push(colCells);
  }
  return grid;
}
